/**
 * ChatSessionContext — selected session id + the agent persona attached to it.
 *
 * RN's `apps/WunderGuide/hooks/useChatSessions.ts` maps `s.agent_avatar_url`
 * and `s.agent_display_name` from the GET /chat/sessions response onto each
 * session so the Dashboard avatar + Chat persona banner come from the API
 * (NOT a hard-coded /brand/leilani.png).
 *
 * Resets on user change (guest→authed signin into a different account).
 * Without this, the cached guest session id leaks into the new user's
 * `/functions/v1/chat?sessionId=…` GET and 401s, since the session belongs
 * to the previous user.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { auth } from '../client';

export interface AgentPersona {
  id?: string;
  /** From `agent_display_name` on the chat-session row. */
  displayName?: string;
  /** From `agent_avatar_url` on the chat-session row. */
  avatarUrl?: string;
  /** Server-resolved localized greeting lines (by time-of-day, en-US fallback);
   * from `greetings` on the chat-session row. {name} token + emoji baked in. */
  greetings?: {
    morning?: string;
    afternoon?: string;
    evening?: string;
    night?: string;
  };
}

/** One chat session = one trip discussion bound to an agent (guide). Mirrors
 * the RN `ChatSession` shape so the multi-location switcher can list/switch. */
export interface ChatSession {
  id: string;
  title?: string;
  agentId?: string;
  agentDisplayName?: string;
  agentAvatarUrl?: string;
  last_message_at?: string;
  location?: string;
  destinationLocation?: {
    lat: number;
    lng: number;
    name?: string;
    timezone?: string;
  };
  // Server-resolved localized greeting lines for the agent's greeting_set,
  // keyed by time-of-day (en-US fallback). Values may contain a {name} token +
  // trailing emoji. Absent when the agent has no greeting_set (→ client default).
  greetings?: {
    morning?: string;
    afternoon?: string;
    evening?: string;
    night?: string;
  };
}

interface ChatSessionContextType {
  selectedSessionId: string | null;
  setSelectedSessionId: (sessionId: string | null) => void;
  agent: AgentPersona | null;
  setAgent: (agent: AgentPersona | null) => void;
  /** All of the user's sessions (one per guide/destination) — drives the switcher. */
  sessions: ChatSession[];
  setSessions: React.Dispatch<React.SetStateAction<ChatSession[]>>;
}

export const ChatSessionContext = createContext<
  ChatSessionContextType | undefined
>(undefined);

export const ChatSessionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const [agent, setAgent] = useState<AgentPersona | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const lastUserIdRef = useRef<string | null>(null);

  // When the JWT subject changes (signin into a different account, or
  // sessionCleared on logout) reset the selected session + agent. Otherwise
  // the previous user's session id leaks into authenticated requests under
  // the new user's token.
  useEffect(() => {
    let cancelled = false;
    auth.getSessionInfo().then(info => {
      if (!cancelled) lastUserIdRef.current = info.userId;
    });
    const unsub = auth.onAuthEvent(async event => {
      try {
        const info = await auth.getSessionInfo();
        const prev = lastUserIdRef.current;
        const next = info.userId;
        if (event === 'sessionCleared' || prev !== next) {
          setSelectedSessionId(null);
          setAgent(null);
          setSessions([]);
        }
        lastUserIdRef.current = next;
      } catch {
        /* noop */
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const value = useMemo(
    () => ({
      selectedSessionId,
      setSelectedSessionId,
      agent,
      setAgent,
      sessions,
      setSessions,
    }),
    [selectedSessionId, agent, sessions],
  );

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  );
};

export const useChatSession = () => {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSession must be used within a ChatSessionProvider');
  }
  return context;
};
